/**
 * ProfilePlaylistsSection — Playlist display, search, follow/unfollow for a profile page.
 *
 * Owns its own Apollo query (GET_MEMBER_PLAYLISTS), follow/unfollow mutations
 * with optimistic UI, and playlist search state.  Receives display context
 * (uid, language, owner status, static text) from CommunityProfilePage.
 */

import React, {
  useMemo, useState, useCallback, useEffect, useRef,
} from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useQuery, useMutation, useApolloClient } from '@apollo/client'
import type { ApolloError } from '@apollo/client'
import { ErrorBoundary } from 'react-error-boundary'
import { usePostHogEvent } from '../../../../PostHog/index'
import { useTranslatedText } from '../../../../StaticText/StaticTextContext'
import { selectAuth } from '../../../../../services/auth/selectors'
import {
  APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
} from '../../../../../services/apollo-graphql/index'
import {
  GET_MEMBER_PLAYLISTS,
  FOLLOW_PLAYLIST,
  UNFOLLOW_PLAYLIST,
} from '../../../../../services/community/query'
import { isProfileVisible, isFieldVisible, getPlaylistDisplayName } from '../../../../../services/community/index'
import { addToasty } from '../../../../../services/toasty/actions'
import { H3 } from '../../../../Heading/index'
import LoadMore from '../../../../LoadMore/LoadMore'
import SearchInput from '../../../../SearchInput/index'
import { ResponsivePlaylistDisplay } from '../../shared/CommunityProfileCarousels/index'
import CommunityPlaylistRowHeader from '../../shared/CommunityPlaylistRowHeader/index'
import CommunityErrorFallback from '../../../shared/ErrorHandling/CommunityErrorFallback/index'
import FollowTag from '../../../shared/FollowTag/index'
import PlaylistShareButton from '../../../shared/PlaylistShareButton/index'
import ProfileReviewBanner from '../ProfileReviewBanner/index'
import {
  buildEnrichedPlaylistDetailsMap,
} from '../../../../../services/community/identity/library/content-utils'
import type { FieldVisibility } from '../../../../../graphql-types'
import type {
  Playlist,
  EnrichedPlaylistItem,
  EnrichedInProgressItem,
  GetMemberPlaylistsResponse,
  FollowPlaylistResponse,
  UnfollowPlaylistResponse,
} from '../../../../../services/community/types'

export interface ProfilePlaylistsSectionProps {
  uid: string
  language: string
  isOwner: boolean
  isLoggedIn: boolean
  profileVisibility: string
  visibilityLevel: string
  /** Currently-watching items, already enriched (from profile.inProgress). */
  inProgressItems?: EnrichedInProgressItem[]
  /** Watch History items, already enriched (from profile.watchHistory). */
  watchHistoryItems?: EnrichedPlaylistItem[]
  /** Apollo error from GET_PROFILE_IN_PROGRESS_CAROUSEL — surfaces inline when empty. */
  inProgressError?: ApolloError
  /** Apollo error from GET_PROFILE_WATCH_HISTORY_CAROUSEL — surfaces inline when empty. */
  watchHistoryError?: ApolloError
  /** UserProfile.currentlyWatchingVisibility — gates the In Progress row for non-owners. */
  currentlyWatchingVisibility?: FieldVisibility | null
  /** UserProfile.watchHistoryVisibility — gates the Watch History row for non-owners. */
  watchHistoryVisibility?: FieldVisibility | null
}

// Only PUBLIC playlists appear on a profile page. ANONYMOUS means
// "share-by-link only" — the owner explicitly opted out of broadcasting
// it on their profile, so even though /playlist/shared/:id serves it to
// anyone with the URL, it must not be listed here. PRIVATE is always
// owner-only and never hits this surface. Module-scoped so the reference
// stays stable across renders (Apollo `variables` equality + useMemo deps).
const VISIBILITY_FILTER: readonly string[] = ['PUBLIC']
const VISIBILITY_FILTER_SET: ReadonlySet<string> = new Set(VISIBILITY_FILTER)

// Offset page size. Larger than the canonical 24 to keep system playlists
// (default / favorites) on page 1 — the client buckets by playlistType and
// the schema has no playlistType where-filter today.
const PAGE_SIZE = 50

const SEARCH_DEBOUNCE_MS = 250

function ProfilePlaylistsSection ({
  uid,
  language,
  isOwner,
  isLoggedIn,
  profileVisibility,
  visibilityLevel,
  inProgressItems = [],
  watchHistoryItems = [],
  inProgressError,
  watchHistoryError,
  currentlyWatchingVisibility = null,
  watchHistoryVisibility = null,
}: ProfilePlaylistsSectionProps) {
  const dispatch = useDispatch()
  const auth = useSelector(selectAuth)
  const jwt = auth.get('jwt')
  const apolloClient = useApolloClient()

  const seeMoreLabel = useTranslatedText({ keys: ['seeMore'] })
  const showLessLabel = useTranslatedText({ keys: ['showLess'] })
  const saveErrorLabel = useTranslatedText({ keys: ['saveError'] })
  const noPlaylistsLabel = useTranslatedText({ keys: ['noPlaylists'] })
  const watchingTitle = useTranslatedText({ keys: ['watchingTitle'] })
  const searchPlaylistsPlaceholder = useTranslatedText({ keys: ['searchPlaylists'] })
  const currentlyWatchingLabel = useTranslatedText({ keys: ['currentlyWatching'] })
  const watchHistoryLabel = useTranslatedText({ keys: ['watchHistory'] })
  const carouselLoadErrorLabel = useTranslatedText({ keys: ['carouselLoadError'] })
  const stWatchLater = useTranslatedText({ keys: ['watchLater'] })
  const stFavorites = useTranslatedText({ keys: ['favorites'] })
  const systemPlaylistNames = useMemo(
    () => ({ watchLater: stWatchLater, favorites: stFavorites }),
    [stWatchLater, stFavorites],
  )

  // ── Search state + debounce ───────────────────────────────────────────
  // `playlistSearch` binds the input; `debouncedSearchTerm` flows into the
  // Apollo variable. Variable change → Apollo re-fetches page 1 automatically.
  const [playlistSearch, setPlaylistSearch] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  useEffect(() => {
    const t = setTimeout(
      () => setDebouncedSearchTerm(playlistSearch.trim()),
      SEARCH_DEBOUNCE_MS,
    )
    return () => clearTimeout(t)
  }, [playlistSearch])

  // ── Apollo: member playlists (page 1) ─────────────────────────────────
  // Offset paginated; searchTerm narrows playlist-name matches server-side.
  // Episode-title matching stays client-side via playlistMatchesSearch below.
  //
  // Pages 2+ bypass Apollo's `fetchMore` / cache-merge pipeline (which
  // intermittently returns no data and re-fires the LoadMore sentinel —
  // the "weird end-of-scroll animation" symptom).  Instead they live in
  // local React state, fed by direct `apolloClient.query({ fetchPolicy:
  // 'no-cache' })` calls.  Same model the Following tab uses in
  // `useFollowedPlaylists.ts`; see that file's docblock for the full
  // rationale, including the three prior `relayStylePagination` /
  // `updateQuery` / `writeQuery` attempts and why each lost merged state.
  //
  // ──────────────────────────────────────────────────────────────────
  // TWIN-CODE — keep in sync (see follow-up ticket for shared extraction):
  //   • `services/community/identity/library/useFollowedPlaylists.ts` —
  //     cursor variant (uses `paginationCursor` instead of
  //     `paginationOffset`)
  //   • `Community/Identity/shared/ConnectionsModal/ConnectionsModal.tsx`
  //     — two cursor instances (followers + following)
  // All three share: page-1 useQuery + pages-2+ no-cache query + local
  // extras accumulator + reset-on-key-change effect + reqGenRef.  When
  // editing any pagination logic here, audit those siblings too.
  // ──────────────────────────────────────────────────────────────────
  const {
    data: playlistsRawData,
    loading: playlistsLoading,
  } = useQuery<GetMemberPlaylistsResponse>(GET_MEMBER_PLAYLISTS, {
    variables: {
      uid,
      language: [language, 'en'],
      visibility: VISIBILITY_FILTER as string[],
      limit: PAGE_SIZE,
      searchTerm: debouncedSearchTerm || undefined,
    },
    skip: !uid || !isProfileVisible({ profileVisibility, visibilityLevel }),
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context: jwt ? { jwt } : {},
  })

  // ── Pages 2+ accumulator ──────────────────────────────────────────────
  // State resets on unmount by design — page 1 rehydrates from the Apollo
  // cache (useQuery `network-only` per Community privacy convention re-fires
  // on remount) and pages 2+ are refetched as the user scrolls.  A user that
  // scrolls deep, navigates away, and comes back will land on page 1 again.
  const [extraPlaylists, setExtraPlaylists] = useState<Playlist[]>([])
  const [paginationOffset, setPaginationOffset] = useState<number>(0)
  const [paginationHasMore, setPaginationHasMore] = useState<boolean>(false)
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false)
  // Synchronous in-flight latch.  React's `isLoadingMore` state can't be
  // trusted as a re-entrancy guard here: LoadMore's IntersectionObserver
  // fires `onLoadMore()` from a vanilla DOM callback, and a second fire can
  // arrive in the same JS tick before React commits `setIsLoadingMore(true)`
  // — both invocations would see the same closure value (`false`) and both
  // would issue `apolloClient.query` with the same `paginationOffset`,
  // double-appending the same items and triggering React's duplicate-key
  // warning.  A ref flips synchronously and blocks the second call before
  // it touches the network.
  const loadMoreInFlightRef = useRef<boolean>(false)

  // Request-generation counter.  Bumped synchronously inside the
  // uid/debouncedSearchTerm reset effect; each `loadMore` invocation
  // captures the current value and bails on resolve if it no longer
  // matches.  Prevents the search-vs-pagination race where a `loadMore`
  // started under filter A resolves *after* the reset effect for filter B
  // runs and prepends stale unfiltered rows onto the new filtered set.
  const reqGenRef = useRef<number>(0)

  // Reset accumulator on uid / debouncedSearchTerm flip — the page-1
  // useQuery refetches automatically when its variables change; this
  // effect clears the local extras so a filtered page 1 isn't mixed with
  // stale unfiltered (or differently-filtered) page-2+ rows.
  useEffect(() => {
    reqGenRef.current += 1
    setExtraPlaylists([])
    setPaginationOffset(0)
    setPaginationHasMore(false)
  }, [uid, debouncedSearchTerm])

  // Seed paginationOffset + paginationHasMore from page 1 once it lands.
  // Guarded by `extraPlaylists.length === 0` so a later page-1 broadcast
  // can't clobber a cursor we've already advanced past.
  useEffect(() => {
    const p1List = playlistsRawData?.playlistList?.playlists ?? []
    const count = playlistsRawData?.playlistList?.count ?? 0
    if (extraPlaylists.length > 0) return
    setPaginationOffset(p1List.length)
    setPaginationHasMore(p1List.length > 0 && p1List.length < count)
  }, [playlistsRawData, extraPlaylists.length])

  const loadMore = useCallback(async (): Promise<void> => {
    if (loadMoreInFlightRef.current || !paginationHasMore) return
    loadMoreInFlightRef.current = true
    // Snapshot the request generation at fire time.  If it changes during
    // the await (search-term flip, uid change), the response is stale —
    // drop it without touching state so we don't prepend unfiltered rows
    // onto the post-reset accumulator.
    const myGen = reqGenRef.current
    setIsLoadingMore(true)
    try {
      const result = await apolloClient.query<GetMemberPlaylistsResponse>({
        query: GET_MEMBER_PLAYLISTS,
        variables: {
          uid,
          language: [language, 'en'],
          visibility: VISIBILITY_FILTER as string[],
          limit: PAGE_SIZE,
          offset: paginationOffset,
          searchTerm: debouncedSearchTerm || undefined,
        },
        fetchPolicy: 'no-cache',
        errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
        context: jwt ? { jwt } : {},
      })
      if (myGen !== reqGenRef.current) return
      const nextList = (result.data?.playlistList?.playlists ?? []) as Playlist[]
      const count = result.data?.playlistList?.count ?? 0
      if (nextList.length > 0) {
        setExtraPlaylists((prev) => [...prev, ...nextList])
      }
      const newOffset = paginationOffset + nextList.length
      setPaginationOffset(newOffset)
      setPaginationHasMore(nextList.length > 0 && newOffset < count)
    } finally {
      loadMoreInFlightRef.current = false
      setIsLoadingMore(false)
    }
    // `apolloClient` is a stable singleton from `useApolloClient()` — listed
    // for exhaustive-deps but never identity-flips in practice.
  }, [
    apolloClient,
    uid,
    language,
    jwt,
    paginationOffset,
    paginationHasMore,
    debouncedSearchTerm,
  ])

  // Drop only the offline downloads manifest — it's locked PRIVATE on the
  // backend so it shouldn't reach us via the PUBLIC visibility filter, but
  // we guard defensively. Watch Later (`'default'`) and Favorites
  // (`'favorites'`) flow through and bucket into their own rows below.
  // Combines page 1 (Apollo cache) + pages 2+ (local accumulator) before
  // filtering so the LoadMore sentinel sees the full accumulated count.
  // The `seen` Set defensively dedupes by `playlist.id` — the
  // `loadMoreInFlightRef` guard above prevents the common concurrent-fire
  // race, but if the backend's offset window ever overlaps (e.g. a row was
  // inserted between two page fetches) we still want React's key
  // invariant to hold.
  const playlistsJS = useMemo(() => {
    const p1List = playlistsRawData?.playlistList?.playlists ?? []
    const combined = [...p1List, ...extraPlaylists]
    const seen = new Set<string>()
    return combined.filter((pl: Playlist) => {
      if (!pl || !pl.id || pl.playlistType === 'offline') return false
      if (seen.has(pl.id)) return false
      seen.add(pl.id)
      return true
    })
  }, [playlistsRawData, extraPlaylists])

  // Belt-and-suspenders client-side filter: drop anything outside PUBLIC +
  // ANONYMOUS (e.g. PRIVATE the server should never send us), and drop empty
  // playlists.
  const visiblePlaylists: Playlist[] = useMemo(() => (
    playlistsJS.filter((p: Playlist) => {
      const vis = (p.visibility || '').toUpperCase()
      const hasItems = (p.playlistItems && p.playlistItems.length > 0)
        || (p.itemCount && p.itemCount > 0)
      return VISIBILITY_FILTER_SET.has(vis) && hasItems
    })
  ), [playlistsJS])

  // ── Playlist follow/unfollow (Apollo mutations + local state) ─────────
  // Server-derived set: recomputes when Apollo refetches, no double-render.
  const serverFollowedIds = useMemo(() => new Set<string>(
    visiblePlaylists.filter((p) => p.isFollowing).map((p) => p.id),
  ), [visiblePlaylists])

  // Local mutations layered on top so optimistic UI survives refetches.
  const [localFollowMutations, setLocalFollowMutations] = useState<
    Map<string, 'added' | 'removed'>
  >(new Map())

  // In-flight guard: prevents rapid-fire follow/unfollow race conditions (RC-1)
  const pendingMutationIdsRef = useRef<Set<string>>(new Set())

  const followedPlaylistIds = useMemo(() => {
    const ids = new Set(serverFollowedIds)
    localFollowMutations.forEach((action, playlistId) => {
      if (action === 'added') ids.add(playlistId)
      else ids.delete(playlistId)
    })
    return ids
  }, [serverFollowedIds, localFollowMutations])

  const [followPlaylistMutation] = useMutation<FollowPlaylistResponse>(FOLLOW_PLAYLIST, {
    context: { jwt },
  })
  const [unfollowPlaylistMutation] = useMutation<UnfollowPlaylistResponse>(UNFOLLOW_PLAYLIST, {
    context: { jwt },
  })

  const trackPlaylistFollowed = usePostHogEvent('community_playlist_followed')
  const trackPlaylistUnfollowed = usePostHogEvent('community_playlist_unfollowed')

  const handleFollowPlaylist = useCallback(async (playlistId: string) => {
    if (pendingMutationIdsRef.current.has(playlistId)) return
    pendingMutationIdsRef.current.add(playlistId)
    setLocalFollowMutations((prev) => new Map(prev).set(playlistId, 'added'))
    try {
      await followPlaylistMutation({ variables: { playlistId } })
      trackPlaylistFollowed(playlistId)
    } catch {
      setLocalFollowMutations((prev) => {
        const next = new Map(prev)
        next.delete(playlistId)
        return next
      })
      dispatch(addToasty(saveErrorLabel))
    } finally {
      pendingMutationIdsRef.current.delete(playlistId)
    }
  }, [followPlaylistMutation, dispatch, saveErrorLabel, trackPlaylistFollowed])

  const handleUnfollowPlaylist = useCallback(async (playlistId: string) => {
    if (pendingMutationIdsRef.current.has(playlistId)) return
    pendingMutationIdsRef.current.add(playlistId)
    setLocalFollowMutations((prev) => new Map(prev).set(playlistId, 'removed'))
    try {
      await unfollowPlaylistMutation({ variables: { playlistId } })
      trackPlaylistUnfollowed(playlistId)
    } catch {
      setLocalFollowMutations((prev) => {
        const next = new Map(prev)
        next.delete(playlistId)
        return next
      })
      dispatch(addToasty(saveErrorLabel))
    } finally {
      pendingMutationIdsRef.current.delete(playlistId)
    }
  }, [unfollowPlaylistMutation, dispatch, saveErrorLabel, trackPlaylistUnfollowed])

  // ── Playlist item enrichment ──────────────────────────────────────────
  // Helper decides series-pivot dedupe per-playlist off `playlistType`.
  // System playlists (Watch Later, Favorites) collapse duplicates; custom
  // playlists owned by the profile owner show every episode.
  const sharedPlaylistDetailsJS = useMemo(
    () => buildEnrichedPlaylistDetailsMap(visiblePlaylists),
    [visiblePlaylists],
  )

  // ── Bucket visible playlists by playlistType ─────────────────────────
  // Order on the page mirrors /playlist/default: Currently Watching → Watch
  // Later → Favorites → custom → Watch History. Watch Later / Favorites
  // surface here only when their visibility is PUBLIC (server-filtered).
  const watchLaterPlaylist = useMemo(
    () => visiblePlaylists.find((p) => p.playlistType === 'default') || null,
    [visiblePlaylists],
  )
  const favoritesPlaylist = useMemo(
    () => visiblePlaylists.find((p) => p.playlistType === 'favorites') || null,
    [visiblePlaylists],
  )
  // Intentionally inline: shared filterUserFacingPlaylists includes default+favorites,
  // which the profile renders separately above via watchLaterPlaylist / favoritesPlaylist.
  const customPlaylists = useMemo(
    () => visiblePlaylists.filter(
      (p) => p.playlistType !== 'default' && p.playlistType !== 'favorites',
    ),
    [visiblePlaylists],
  )

  // ── Visibility gates for the activity surfaces ───────────────────────
  // visibilityLevel is normalized to 'member' for owners upstream — same
  // pattern as bioVisibility. Owners who set their flag to 'private' won't
  // see the section here; they edit on /playlist/default.
  const showInProgressGate = inProgressItems.length > 0
    && isFieldVisible(currentlyWatchingVisibility, visibilityLevel)
  const showWatchHistoryGate = watchHistoryItems.length > 0
    && isFieldVisible(watchHistoryVisibility, visibilityLevel)

  // ── Search filtering across all 5 row types ──────────────────────────
  // `playlistSearch` is declared above (alongside the debounced variable that
  // drives the server-side `searchTerm`). `q` is the client-side filter used
  // for episode-title matching on the loaded set — a layer on top of the
  // server-narrowed corpus.
  const q = playlistSearch.toLowerCase()

  const filteredInProgress = useMemo(() => {
    if (!showInProgressGate) return []
    if (!q) return inProgressItems
    return inProgressItems.filter((item) => (item.title || '').toLowerCase().includes(q))
  }, [showInProgressGate, inProgressItems, q])

  const filteredWatchHistory = useMemo(() => {
    if (!showWatchHistoryGate) return []
    if (!q) return watchHistoryItems
    return watchHistoryItems.filter(
      (item) => (item.title || '').toLowerCase().includes(q),
    )
  }, [showWatchHistoryGate, watchHistoryItems, q])

  const playlistMatchesSearch = useCallback(
    (playlist: Playlist, items: EnrichedPlaylistItem[]): boolean => {
      if (!q) return true
      const displayName = getPlaylistDisplayName(playlist, systemPlaylistNames).toLowerCase()
      if (displayName.includes(q)) return true
      return items.some((item) => (item.title || '').toLowerCase().includes(q))
    },
    [q, systemPlaylistNames],
  )

  const watchLaterMatches = useMemo(() => {
    if (!watchLaterPlaylist) return null
    const items = sharedPlaylistDetailsJS[watchLaterPlaylist.id]?.items || []
    return playlistMatchesSearch(watchLaterPlaylist, items)
      ? { playlist: watchLaterPlaylist, items }
      : null
  }, [watchLaterPlaylist, sharedPlaylistDetailsJS, playlistMatchesSearch])

  const favoritesMatches = useMemo(() => {
    if (!favoritesPlaylist) return null
    const items = sharedPlaylistDetailsJS[favoritesPlaylist.id]?.items || []
    return playlistMatchesSearch(favoritesPlaylist, items)
      ? { playlist: favoritesPlaylist, items }
      : null
  }, [favoritesPlaylist, sharedPlaylistDetailsJS, playlistMatchesSearch])

  const filteredCustom = useMemo(
    () => customPlaylists.filter((p) => playlistMatchesSearch(
      p,
      sharedPlaylistDetailsJS[p.id]?.items || [],
    )),
    [customPlaylists, sharedPlaylistDetailsJS, playlistMatchesSearch],
  )

  // Render the error variant when the carousel query errored AND no items
  // resolved (errorPolicy:'all' may still surface partial data; if items
  // arrived, prefer rendering them).
  const showInProgressError = !!inProgressError && filteredInProgress.length === 0
  const showWatchHistoryError = !!watchHistoryError && filteredWatchHistory.length === 0

  const hasAnyRow = filteredInProgress.length > 0
    || !!watchLaterMatches
    || !!favoritesMatches
    || filteredCustom.length > 0
    || filteredWatchHistory.length > 0
    || showInProgressError
    || showWatchHistoryError

  // ── Empty state ─────────────────────────────────────────────────────
  // Owners with no shared playlists see the "Show what you're exploring" banner.
  // Non-owners (or while still loading) see the plain no-playlists text.
  //
  // CTA choice for the owner case is driven by profileVisibility:
  //   - Not private (public / membersOnly): "View your Library" → /playlist/default
  //     — they can already share, the next concrete step is adding content.
  //   - Private: keep the default "View your Profile" → /profile/edit — they
  //     need to make the profile shareable before adding playlists matters.
  const ownCtaKind: 'viewProfile' | 'viewLibrary' = profileVisibility !== 'private'
    ? 'viewLibrary'
    : 'viewProfile'

  const emptyState = isOwner && !playlistsLoading
    ? <ProfileReviewBanner variant="ownProfile" ctaKind={ownCtaKind} />
    : (
      <span className="community-profile-member__card-body">
        {noPlaylistsLabel}
      </span>
    )

  // ── Per-row render helpers ──────────────────────────────────────────
  const renderPlaylistRow = (
    playlist: Playlist,
    items: EnrichedPlaylistItem[],
  ) => {
    const isPlaylistFollowed = followedPlaylistIds.has(playlist.id)
    const details = sharedPlaylistDetailsJS[playlist.id]
    const displayName = getPlaylistDisplayName(playlist, systemPlaylistNames)
    return (
      <div key={playlist.id} className="community-profile-member__playlist-row">
        <CommunityPlaylistRowHeader
          name={displayName}
          badge={null}
          episodeCount={playlist.itemCount || 0}
          totalDuration={details ? details.totalDuration : null}
          authorDisplay={null}
          authorUid={null}
          statusTag={isOwner ? null : (
            <FollowTag
              isFollowing={isPlaylistFollowed}
              followerCount={playlist.followerCount || 0}
              onClick={!isLoggedIn || isPlaylistFollowed
                ? undefined
                : () => handleFollowPlaylist(playlist.id)}
              onUnfollow={!isLoggedIn
                ? undefined
                : () => handleUnfollowPlaylist(playlist.id)}
              disabled={!isLoggedIn}
            />
          )}
          shareButton={(
            <PlaylistShareButton
              playlistId={playlist.id}
              playlistName={displayName}
              description={playlist.description}
              items={items}
            />
          )}
        />
        {items.length > 0 && (
          <ResponsivePlaylistDisplay
            items={items}
            seeMoreLabel={seeMoreLabel}
            showLessLabel={showLessLabel}
          />
        )}
      </div>
    )
  }

  // Activity-surface row — no FollowTag, no shareButton (these are profile
  // flags, not user playlists with a /playlist/shared/<id> URL).  When the
  // backing query errored with no items, render an inline message inside
  // the same row so the section header still appears (rather than letting
  // the carousel silently disappear).
  const renderActivityRow = (
    key: 'currently-watching' | 'watch-history',
    label: string,
    items: EnrichedPlaylistItem[] | EnrichedInProgressItem[],
    error?: ApolloError,
  ) => (
    <div key={key} className="community-profile-member__playlist-row">
      <CommunityPlaylistRowHeader
        name={label}
        badge={null}
        episodeCount={items.length}
        totalDuration={null}
        authorDisplay={null}
        authorUid={null}
      />
      {items.length > 0 ? (
        <ResponsivePlaylistDisplay
          items={items}
          seeMoreLabel={seeMoreLabel}
          showLessLabel={showLessLabel}
        />
      ) : null}
      {items.length === 0 && error ? (
        <p
          className="community-profile-member__carousel-error"
          data-carousel-error={key}
        >
          {carouselLoadErrorLabel}
        </p>
      ) : null}
    </div>
  )

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <ErrorBoundary FallbackComponent={CommunityErrorFallback}>
      <div className="community-profile-member__section">
        <H3 className="community-profile-member__section-title">
          {watchingTitle}
        </H3>
        <SearchInput
          value={playlistSearch}
          onChange={(e) => setPlaylistSearch(e.target.value)}
          placeholder={searchPlaylistsPlaceholder}
          className="playlist-tab-container__search"
        />
        {hasAnyRow ? (
          <>
            {/* 1. Currently Watching */}
            {filteredInProgress.length > 0 || showInProgressError
              ? renderActivityRow(
                'currently-watching',
                currentlyWatchingLabel,
                filteredInProgress,
                showInProgressError ? inProgressError : undefined,
              )
              : null}

            {/* 2. Watch Later */}
            {watchLaterMatches
              ? renderPlaylistRow(watchLaterMatches.playlist, watchLaterMatches.items)
              : null}

            {/* 3. Favorites */}
            {favoritesMatches
              ? renderPlaylistRow(favoritesMatches.playlist, favoritesMatches.items)
              : null}

            {/* 4. Custom playlists */}
            {filteredCustom.map((playlist) => renderPlaylistRow(
              playlist,
              sharedPlaylistDetailsJS[playlist.id]?.items || [],
            ))}

            {/* Load-more sentinel between custom playlists and Watch History.
                Gated on `paginationHasMore` so the IntersectionObserver
                unmounts when the accumulator exhausts — `LoadMore.jsx`'s
                internal `currentCount === totalCount` quiet condition can't
                fire here because the counts come from different filter passes. */}
            {paginationHasMore && playlistsJS.length > 0 ? (
              // @ts-expect-error LoadMore's default export carries a bogus `@type {LoadMore}`
              // JSDoc (lib/components/LoadMore/LoadMore.jsx) that types the connected export as a
              // class instance, not a component. Suppressed here to avoid editing shared infra.
              <LoadMore
                onLoadMore={loadMore}
                currentCount={playlistsJS.length}
                totalCount={playlistsRawData?.playlistList?.count ?? 0}
                processing={isLoadingMore}
                infiniteScroll
                buttonClassName={['community-profile-member__load-more-button']}
              />
            ) : null}

            {/* 5. Watch History */}
            {filteredWatchHistory.length > 0 || showWatchHistoryError
              ? renderActivityRow(
                'watch-history',
                watchHistoryLabel,
                filteredWatchHistory,
                showWatchHistoryError ? watchHistoryError : undefined,
              )
              : null}
          </>
        ) : emptyState}
      </div>
    </ErrorBoundary>
  )
}

export default ProfilePlaylistsSection
